import {
  Box,
  Checkbox,
  Divider,
  Grid2 as Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material';
import { format } from 'date-fns';
import { BasicDocumentValueItem, BoxWithHeader, LineClamp, TransparentChip } from '../common';
import { ACCIDENT_FILES, Accident, AccidentUserType } from '../types/accident';
import { GenerableDocument } from '../types/rental';
import { AccidentsPhotos } from './AccidentsPhotos';
import { DocumentActions } from './DocumentActions';
import { isStkExpired } from './utils/utils';

interface AccidentCreatingStepRecapProps {
  accident: Accident;
  setSelectedDocumentName: (template: ACCIDENT_FILES) => void;
  handleGenerateDocument?: (template: ACCIDENT_FILES) => void;
  accidentDocuments?: GenerableDocument[];
  isGeneratingDocument?: boolean;
}

export const AccidentCreatingStepRecap = ({
  accident,
  accidentDocuments,
  setSelectedDocumentName,
  handleGenerateDocument,
  isGeneratingDocument = false
}: AccidentCreatingStepRecapProps) => (
  <>
    <BoxWithHeader title="Dokumenty k podepsání" mt={2}>
      <Stack gap={2}>
        {[
          {
            title: 'Plná moc',
            template: ACCIDENT_FILES.POWER_OF_ATTORNEY
          }
        ].map(({ title, template }) => {
          const document = accidentDocuments?.find((doc) => doc.template === template);
          if (!document) return;
          return (
            <Stack key={template}>
              <LineClamp maxLines={1} fontWeight="bold">
                {title}
              </LineClamp>
              <Box my={1}>
                <Divider />
              </Box>
              <Stack alignItems="center" direction="row" justifyContent="space-between" width={1}>
                <BasicDocumentValueItem
                  fileName={title}
                  url={document.path}
                  createdDate={document?.updatedDate}
                  component={document.path ? 'a' : 'p'}
                />
                <Stack direction="row">
                  <DocumentActions
                    document={document}
                    template={document.template as ACCIDENT_FILES}
                    isGeneratingDocument={isGeneratingDocument}
                    handleGenerateDocument={(template) => handleGenerateDocument?.(template as ACCIDENT_FILES)}
                    setSelectedDocumentName={(template) => setSelectedDocumentName(template)}
                  />
                </Stack>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </BoxWithHeader>
    <BoxWithHeader title="Rekapitulace" mt={2}>
      <Grid container>
        <Grid size={{ xs: 12, lg: 6 }}>
          <TableContainer>
            <Table aria-label="Recap table">
              <TableBody>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Značka vozu
                  </TableCell>
                  <TableCell align="right">{accident.car?.brand}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Model vozu
                  </TableCell>
                  <TableCell align="right">{accident.car?.model}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    SPZ
                  </TableCell>
                  <TableCell align="right">{accident.car?.plateNumber}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    VIN
                  </TableCell>
                  <TableCell align="right">{accident.car?.vin}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Platnost STK
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      {accident.car?.stk && <span>{format(accident.car.stk, 'dd.MM.yyyy')}</span>}
                      {isStkExpired(accident.car?.stk, accident?.date) && (
                        <TransparentChip label="STK je prošlá" color="error" variant="filled" />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Místo nehody
                  </TableCell>
                  <TableCell align="right">{accident?.place}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Datum nehody
                  </TableCell>
                  <TableCell align="right">{accident?.date && format(accident.date, 'dd.MM.yyyy HH:mm')}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Bylo vozidlo před nehodou poškozeno
                  </TableCell>
                  <TableCell align="right">
                    <Checkbox checked={!!accident?.car?.wasDamagedBefore} readOnly />
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Jedná se o totální škodu
                  </TableCell>
                  <TableCell align="right">
                    <Checkbox checked={!!accident?.car?.isTotalCrash} readOnly />
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Místo, kde se automobil nachází
                  </TableCell>
                  <TableCell align="right">{accident?.car?.location}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Kontaktní osoba v případě prohlídky
                  </TableCell>
                  <TableCell align="right">{accident?.car?.contactPerson}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Finanční leasing
                  </TableCell>
                  <TableCell align="right">
                    <Checkbox checked={!!accident?.car?.hasLeasing} readOnly />
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Číslo leasingové / úvěrové smlouvy
                  </TableCell>
                  <TableCell align="right">{accident?.car?.leasingNumber}</TableCell>
                </TableRow>

                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Parkovné
                  </TableCell>
                  <TableCell align="right">
                    <Checkbox checked={!!accident?.hasParking} readOnly />
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Odtah
                  </TableCell>
                  <TableCell align="right">
                    <Checkbox checked={!!accident?.hasTowing} readOnly />
                  </TableCell>
                </TableRow>

                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Řešeno policií
                  </TableCell>
                  <TableCell align="right">
                    <Checkbox checked={!!accident?.isPoliceHandled} readOnly />
                  </TableCell>
                </TableRow>

                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Byla pořízena fotodokumentace
                  </TableCell>
                  <TableCell align="right">
                    <Checkbox checked={!!accident?.hasPhotos} readOnly />
                  </TableCell>
                </TableRow>

                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Kde poslat peníze
                  </TableCell>
                  <TableCell align="right">
                    {accident?.payoutType === 'address' ? 'Adresa trvalého bydliště' : 'Bankovní účet'}
                  </TableCell>
                </TableRow>

                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    č.ú. / adresa trvalého bydliště
                  </TableCell>
                  <TableCell align="right">{accident?.payoutInfo}</TableCell>
                </TableRow>

                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Poznámka k nehodě
                  </TableCell>
                  <TableCell align="right">{accident?.note}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Interní poznámka
                  </TableCell>
                  <TableCell align="right">{accident?.internalNote}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <TableContainer>
            <Table aria-label="Recap table">
              <TableBody>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Majitel vozidla
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  {accident.user?.type?.name === AccidentUserType.Company ? (
                    <>
                      <TableCell component="th" scope="row">
                        Název firmy, IČ, DIČ
                      </TableCell>
                      <TableCell align="right">
                        {[
                          accident.user?.company?.contactDegree,
                          accident.user?.company?.companyName,
                          accident.user?.company?.companyIdNum,
                          accident.user?.company?.companyTaxNum
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell component="th" scope="row">
                        Jméno, příjmení, datum narození
                        {accident.user?.type?.name === AccidentUserType.BusinessMan ? ', IČ' : ''}
                      </TableCell>
                      <TableCell align="right">
                        {[
                          accident.user?.degree,
                          accident.user?.name,
                          accident.user?.surname,
                          accident.user?.birthDate && format(accident.user.birthDate, 'dd.MM.yyyy'),
                          accident.user?.type?.name === AccidentUserType.BusinessMan
                            ? accident.user?.company?.companyIdNum
                            : undefined
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      </TableCell>
                    </>
                  )}
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Provozovatel vozidla
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  {accident.operator?.type?.name === AccidentUserType.Company ? (
                    <>
                      <TableCell component="th" scope="row">
                        Název firmy, IČ, DIČ
                      </TableCell>
                      <TableCell align="right">
                        {[
                          accident.operator?.company?.contactDegree,
                          accident.operator?.company?.companyName,
                          accident.operator?.company?.companyIdNum,
                          accident.operator?.company?.companyTaxNum
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell component="th" scope="row">
                        Jméno, příjmení, datum narození
                        {accident.operator?.type?.name === AccidentUserType.BusinessMan ? ', IČ' : ''}
                      </TableCell>
                      <TableCell align="right">
                        {[
                          accident.operator?.degree,
                          accident.operator?.name,
                          accident.operator?.surname,
                          accident.operator?.birthDate && format(accident.operator.birthDate, 'dd.MM.yyyy'),
                          accident.operator?.type?.name === AccidentUserType.BusinessMan
                            ? accident.operator?.company?.companyIdNum
                            : undefined
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      </TableCell>
                    </>
                  )}
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Řidič vozidla
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    Jméno, příjmení, datum narození
                  </TableCell>
                  <TableCell align="right">
                    {[
                      accident.driver?.degree,
                      accident.driver?.name,
                      accident.driver?.surname,
                      accident.driver?.birthDate && format(accident.driver.birthDate, 'dd.MM.yyyy')
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    Rodné číslo
                  </TableCell>
                  <TableCell align="right">{accident.driver?.birthNumber}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    Kontaktní osoba
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    Jméno, příjmení, telefon
                  </TableCell>
                  <TableCell align="right">
                    {[
                      accident?.contactPerson?.degree,
                      accident?.contactPerson?.name,
                      accident?.contactPerson?.surname,
                      accident?.contactPerson?.birthNumber,
                      accident?.contactPerson?.phone
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    Rodné číslo
                  </TableCell>
                  <TableCell align="right">{accident.contactPerson?.birthNumber}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </BoxWithHeader>
    <BoxWithHeader title="Fotky havarovaného vozu" mt={2}>
      <Grid container mt={3} spacing={10}>
        {accident?.media?.images && <AccidentsPhotos photos={accident?.media?.images} />}
      </Grid>
    </BoxWithHeader>
  </>
);
